import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { Stethoscope, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const DentalHubAdminLoginPage = () => {
  const [formData, setFormData] = useState({ 
    email: 'neinususa@gmail.com', 
    password: 'Sathy@638401' 
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please enter email and password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest('admin_login.php', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      if (response.success) {
        // Store admin session
        localStorage.setItem('admin_session_token', response.session_token);
        localStorage.setItem('admin_user', JSON.stringify(response.admin));
        
        toast({
          title: 'Welcome!',
          description: 'Successfully logged in as Dental Hub Admin.',
        });
        
        navigate('/admin/hub/dashboard');
      } else {
        throw new Error(response.error || response.message || 'Login failed');
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid email or password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dental Hub Admin Login</title>
        <meta name="description" content="Login to Dental Hub admin panel to manage hospitals and subscriptions." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                <span className="text-xl sm:text-2xl font-bold gradient-text">Dental Hub Admin</span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Platform Administration</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Sign in to manage hospitals and subscriptions
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="neinususa@gmail.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-white">Password</Label>
                    <Link 
                      to="/admin/hub/forgot-password" 
                      className="text-blue-400 hover:text-blue-300 underline text-sm transition-colors"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <p className="text-gray-400 text-sm">
                  Don't have an account?{' '}
                  <Link 
                    to="/admin/hub/register" 
                    className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    Register
                  </Link>
                </p>
                <a 
                  href="/" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm block"
                >
                  ← Back to Home
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default DentalHubAdminLoginPage;

